/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAuthentication } from '@/hooks/useAuthentication';
import { useBookmarkStore } from '@/pages/bookmarks/store/useBookmarkStore';
import { addBookmark, existBookmark, findBookmark, removeBookmark } from '@/services/bookmark/bookmark.service';
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Course,
  GENERATED,
  KnowledgeBase,
  FetchMyBookmarksQuery,
  FetchMyBookmarksQueryVariables,
  QUERY_MY_BOOKMARKS,
  Podcast,
  Resource,
} from '@edwin-group/people-graphql';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { useDebounce } from '@/hooks/useDebounce';
import { useQueryCacheAndNetwork } from '@/hooks/useQueryCacheAndNetwork';

export default function useBookmark({ relationId }: { relationId: string }) {
  const [exist, setExist] = useState<boolean>(false);
  const { authentication } = useAuthentication();
  const { removeFromBookmark, page, onInitPage } = useBookmarkStore();

  const getTitle = (metadata: KnowledgeBase | Course) => {
    if (metadata?.__typename === 'KnowledgeBase') {
      return metadata?.title ?? metadata?.attributes?.title ?? '';
    }

    if (metadata?.__typename === 'Course') {
      return metadata?.title ?? metadata?.attributes?.title ?? '';
    }

    return '';
  };

  const handleClick = async (
    metadata: any,
    typeId?: string,
    category?: GENERATED.Enum_Bookmark_Category,
    onRemoveFromBookmark?: () => void,
  ) => {
    try {
      if (exist) {
        const { data } = await findBookmark(relationId);
        if (!data?.bookmarks?.data[0]?.id) return console.error('Bookmark not found');
        await removeBookmark(data?.bookmarks?.data[0]?.documentId || '');
        await removeFromBookmark(category || GENERATED.Enum_Bookmark_Category.KnowledgeBase);
        setExist(false);

        if (onRemoveFromBookmark) {
          onRemoveFromBookmark();
        }
      } else {
        trackGAEvent('bookmarked', {
          type: category ?? '',
          name: getTitle(metadata),
        });
        await addBookmark({
          category,
          metadata: JSON.stringify(metadata),
          relationId,
          ...(category === GENERATED.Enum_Bookmark_Category.KnowledgeBase && typeId
            ? { knowledgeBaseType: typeId }
            : {}),
          staff: `${authentication?.user?.documentId}`,
        });
        setExist(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    (async () => {
      await onInitPage();
    })();
  }, [onInitPage]);

  useEffect(() => {
    (async () => {
      if (!relationId) return;
      const { data } = await existBookmark(relationId);
      setExist((data?.bookmarks?.data?.length || 0) > 0);
    })();
  }, [relationId]);

  return { exist, page, handleClick };
}

export type BookmarkTab = 'KNOWLEDGE_BASE' | 'COURSE' | 'PODCAST' | 'RESOURCE';

export interface BookmarkFilters {
  search: string;
  category: BookmarkTab | null;
  knowledgeBaseType?: string;
}

export type BookmarkItem = {
  relationId: string;
  category: string;
  metadata: Course | KnowledgeBase | Podcast | Resource;
  knowledgeBaseType?: { type: string } | null;
};

export interface UseBookmarkFiltersReturn {
  allBookmarks: BookmarkItem[];
  filteredBookmarks: BookmarkItem[];
  availableCategories: BookmarkTab[];
  types: string[];
  isLoading: boolean;
  isRefetching: boolean;
  filters: BookmarkFilters;
  pagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCategoryChange: (category: BookmarkTab) => void;
  onKnowledgeBaseTypeChange: (type: string | undefined) => void;
  onPageChange: (page: number) => void;
  refetch: () => void;
}

const DEFAULT_PAGE_SIZE = 8;
const FETCH_ALL_PAGE_SIZE = 1000;

const CATEGORY_ORDER: BookmarkTab[] = ['RESOURCE', 'KNOWLEDGE_BASE', 'PODCAST', 'COURSE'];

export function useBookmarkFilters(initialFilters?: Partial<BookmarkFilters>): UseBookmarkFiltersReturn {
  const [filters, setFilters] = useState<BookmarkFilters>({
    search: initialFilters?.search ?? '',
    category: initialFilters?.category ?? null,
    knowledgeBaseType: initialFilters?.knowledgeBaseType,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  const handleSearchCallback = useCallback((text: string) => {
    setDebouncedSearch(text);
    setCurrentPage(1);
  }, []);

  const { value: searchValue, onChangeValue: onSearchChange } = useDebounce(handleSearchCallback, 300, filters.search);

  const queryVariables = useMemo<FetchMyBookmarksQueryVariables>(() => {
    const vars: FetchMyBookmarksQueryVariables = {
      pageSize: FETCH_ALL_PAGE_SIZE,
    };

    if (debouncedSearch.trim()) {
      vars.search = debouncedSearch.trim();
    }

    if (filters.knowledgeBaseType) {
      vars.knowledgeBaseType = filters.knowledgeBaseType;
    }

    return vars;
  }, [filters.knowledgeBaseType, debouncedSearch]);

  const { data, isInitialLoading, isRefetching, refetch } = useQueryCacheAndNetwork<
    FetchMyBookmarksQuery,
    FetchMyBookmarksQueryVariables
  >(QUERY_MY_BOOKMARKS, {
    variables: queryVariables,
  });

  const allBookmarks = useMemo<BookmarkItem[]>(() => {
    return (
      data?.fetchMyBookmarks?.results?.map((result) => ({
        relationId: result?.relationId ?? '',
        category: result?.category ?? '',
        metadata: result?.metadata as Course | KnowledgeBase | Podcast | Resource,
        knowledgeBaseType: result?.knowledgeBaseType,
      })) ?? []
    );
  }, [data]);

  const availableCategories = useMemo<BookmarkTab[]>(() => {
    const categoriesSet = new Set<string>();
    allBookmarks.forEach((bookmark) => {
      if (bookmark.category) {
        categoriesSet.add(bookmark.category);
      }
    });
    return CATEGORY_ORDER.filter((cat) => categoriesSet.has(cat));
  }, [allBookmarks]);

  const filteredByCategory = useMemo<BookmarkItem[]>(() => {
    if (!filters.category) {
      return allBookmarks;
    }
    return allBookmarks.filter((bookmark) => bookmark.category === filters.category);
  }, [allBookmarks, filters.category]);

  const paginatedBookmarks = useMemo<BookmarkItem[]>(() => {
    const start = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    return filteredByCategory.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [filteredByCategory, currentPage]);

  const types = useMemo<string[]>(() => {
    return data?.fetchMyBookmarks?.types?.filter((t): t is string => t !== null) ?? [];
  }, [data]);

  const pagination = useMemo(() => {
    return {
      page: currentPage,
      pageSize: DEFAULT_PAGE_SIZE,
      pageCount: Math.ceil(filteredByCategory.length / DEFAULT_PAGE_SIZE),
      total: filteredByCategory.length,
    };
  }, [currentPage, filteredByCategory.length]);

  const onCategoryChange = useCallback((category: BookmarkTab) => {
    setFilters((prev) => ({
      ...prev,
      category,
      knowledgeBaseType: undefined,
    }));
    setCurrentPage(1);
  }, []);

  const onKnowledgeBaseTypeChange = useCallback((type: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      knowledgeBaseType: type,
    }));
    setCurrentPage(1);
  }, []);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return {
    allBookmarks,
    filteredBookmarks: paginatedBookmarks,
    availableCategories,
    types,
    isLoading: isInitialLoading,
    isRefetching,
    filters,
    pagination,
    searchValue,
    onSearchChange,
    onCategoryChange,
    onKnowledgeBaseTypeChange,
    onPageChange,
    refetch,
  };
}
